import { useMemo, useState } from "react";
import {
  ShipmentStopType,
  ShipmentType,
} from "@api/graphql/generated/generated-types";
import {
  StopOrderHint,
  getCompletedStopIds,
  getPendingExistingStopIds,
  getStopOrderMode,
  hasPendingStopOrderChange,
  isDisplayedOrderConsistentWithStoredOrder,
  isRouteBasedOrder,
} from "../utils/stopOrderRules";
import { toStopOrderItems } from "../utils/stopOrderRowRules";
import { EditOrderShipment } from "@src/shipments/components/EditOrderModalComponent/hooks/useEditOrderShipment";

interface UseStopOrderEditingProps {
  shipment: EditOrderShipment;
  shouldKeepOrderEntrySequence: boolean;
  shipmentType: ShipmentType | undefined;
  formStops: Array<{ id?: string; type: ShipmentStopType }> | undefined;
}

type InitialStops = Array<{ id: string; type: ShipmentStopType }>;

/**
 * The stop order state of the Edit Order modal: the order the stops started in,
 * whether the dispatcher changed it, and the hint Confirm Route opens with.
 */
export function useStopOrderEditing({
  shipment,
  shouldKeepOrderEntrySequence,
  shipmentType,
  formStops,
}: UseStopOrderEditingProps) {
  const [baselineStopIds, setBaselineStopIds] = useState<string[]>([]);
  const [baselineDisplayedStopIds, setBaselineDisplayedStopIds] = useState<
    string[]
  >([]);
  const [stopOrderHint, setStopOrderHint] = useState<StopOrderHint | null>(null);
  const completedStopIds = useMemo(
    () => getCompletedStopIds(shipment?.stops ?? []),
    [shipment]
  );
  const stopOrderMode = getStopOrderMode({
    shouldKeepOrderEntrySequence,
    shipmentType: shipmentType ?? shipment?.type,
    shipmentStatus: shipment?.status,
    hasDriver: !!shipment?.driver,
    isRouteBased: isRouteBasedOrder(shipment?.stops ?? []),
    isStoredOrderConsistent: isDisplayedOrderConsistentWithStoredOrder(
      baselineDisplayedStopIds,
      shipment?.stops ?? []
    ),
  });
  const hasStopOrderChange =
    stopOrderMode !== "hidden" &&
    hasPendingStopOrderChange(
      baselineStopIds,
      toStopOrderItems(formStops ?? [], completedStopIds)
    );

  const recordBaselineStopOrder = (initialStops: InitialStops) => {
    setBaselineStopIds(
      getPendingExistingStopIds(toStopOrderItems(initialStops, completedStopIds))
    );
    setBaselineDisplayedStopIds(initialStops.map((stop) => stop.id));
  };

  return {
    stopOrderEditing:
      stopOrderMode === "hidden"
        ? undefined
        : { mode: stopOrderMode, baselineStopIds, setStopOrderHint },
    stopOrderHint,
    hasStopOrderChange,
    recordBaselineStopOrder,
    clearStopOrderHint: () => setStopOrderHint(null),
  };
}
